import React, { useState, useEffect } from 'react';
import {
  Button,
  Container,
  Box,
  Modal,
  Tooltip,
  TextField,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

// Static data for other income items
const staticOtherIncomeItemData = [
  { incomeItemId: 1, groupName: 'Investments', itemName: 'Stock Dividends', remark: 'Dividends from stock investments' },
  { incomeItemId: 2, groupName: 'Rental Income', itemName: 'Residential Rent', remark: 'Income from residential rentals' },
  { incomeItemId: 3, groupName: 'Freelance', itemName: 'Consulting Fees', remark: 'Fees from consulting services' },
  { incomeItemId: 4, groupName: 'Dividends', itemName: 'Bond Interest', remark: 'Interest from bond investments' },
  { incomeItemId: 5, groupName: 'Royalties', itemName: 'Book Royalties', remark: 'Royalties from book sales' },
  { incomeItemId: 6, groupName: 'Gifts', itemName: 'Cash Gifts', remark: 'Cash gifts received' },
];

// Static data for other income groups
const incomeGroups = [
  { id: 1, name: 'Investments' },
  { id: 2, name: 'Rental Income' },
  { id: 3, name: 'Freelance' },
  { id: 4, name: 'Dividends' },
  { id: 5, name: 'Royalties' },
  { id: 6, name: 'Gifts' },
];

const EditOtherIncomeItem = () => {
  const { incomeItemId } = useParams();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formData, setFormData] = useState({
    groupName: '',
    itemName: '',
    remark: '',
  });

  const navigate = useNavigate();

  useEffect(() => {
    // Simulate fetching data for the income item to edit
    const incomeItem = staticOtherIncomeItemData.find(
      (item) => item.incomeItemId === parseInt(incomeItemId)
    );
    if (incomeItem) {
      setFormData({
        groupName: incomeItem.groupName,
        itemName: incomeItem.itemName,
        remark: incomeItem.remark,
      });
    }
  }, [incomeItemId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    // Check if all required fields are filled
    if (!formData.groupName || !formData.itemName || !formData.remark) {
      alert('Please fill all fields');
      return;
    }
    setShowSuccessModal(true);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Container>
      <Box
        sx={{
          backgroundColor: 'rgba(2, 78, 101, 1)',
          padding: '12px 16px',
          boxShadow: 3,
          borderRadius: '4px 4px 0 0',
          margin: '0 auto',
          width: '100%',
        }}
      >
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
          Edit Other Income Item
        </Typography>
      </Box>
      <Paper elevation={3} style={{ padding: 16, width: '100%', margin: 'auto', marginTop: 6, backgroundColor: '#f8fdfeff' }}>
        <form onSubmit={handleSave}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="income-group-label">Other Income Group</InputLabel>
              <Select
                labelId="income-group-label"
                id="groupName"
                name="groupName"
                value={formData.groupName}
                label="Other Income Group"
                onChange={handleChange}
              >
                {incomeGroups.map((group) => (
                  <MenuItem key={group.id} value={group.name}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Other Income Item"
              variant="outlined"
              fullWidth
              name="itemName"
              value={formData.itemName}
              onChange={handleChange}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Remark"
              variant="outlined"
              fullWidth
              name="remark"
              value={formData.remark}
              onChange={handleChange}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip
              title={
                (!formData.groupName && 'Select Other Income Group') ||
                (!formData.itemName && 'Enter Other Income Item') ||
                (!formData.remark && 'Enter Remark')
              }
              arrow
            >
              <Button
                variant="contained"
                type="submit"
                sx={{ mr: 2, backgroundColor: '#127498ff', '&:hover': { backgroundColor: '#116280ff' } }}
                disabled={!formData.groupName || !formData.itemName || !formData.remark}
              >
                SAVE
              </Button>
            </Tooltip>
            <Button variant="contained" color="error" onClick={handleCancel}>
              CANCEL
            </Button>
          </Box>
        </form>
      </Paper>
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Other Income Item Updated ✅
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              Other Income Item Updated Successfully
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1);
                }}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

export default EditOtherIncomeItem;
